import React from 'react';
import { Card, CardContent, Grid, Typography, Divider, Paper, Button, Box } from '@mui/material';
import { useAppDispatch, useAppSelector } from 'redux/hooks/reduxHooks';
import { openCurrentModal } from 'redux/slices/modals/modals.slice';
import { EModalsNames } from 'shared/types/modal.interface';
import { TopicSuggestion } from 'redux/api/topics.api';
import { TOPICS_CONSTANTS } from 'shared/constants/topics.constants';
import { GenerateTopicsForm } from 'components/Forms/GenerateTopicsForm/GenerateTopicsForm';
import PageContainer from 'pages/PageContainer';
import { LOCAL_STORAGE } from 'shared/constants/local-storage-constants';
import { useAuth } from 'shared/guards/useAuth';

const GenerateTopics: React.FC = () => {
  useAuth();
  localStorage.removeItem(LOCAL_STORAGE.toGenerateWithAI);
  const dispatch = useAppDispatch();
  const { suggestions, error } = useAppSelector((state) => state.app.topics);

  const handleCreateDebate = (suggestion: TopicSuggestion) => {
    dispatch(
      openCurrentModal({
        popupName: EModalsNames.CreateDebate,
        data: {
          title: suggestion.thesis,
          subtitle: suggestion.description,
        },
      }),
    );
  };

  return (
    <PageContainer showMenu={false}>
      <Box maxWidth="1200px" mx="auto">
        <Paper elevation={2} sx={{ p: 4, mb: 5, borderRadius: 2 }}>
          <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 'bold', mb: 3 }}>
            {TOPICS_CONSTANTS.PAGE_TITLE}
          </Typography>
          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2 }}>
            You can generate debate topics based on the information on the web page by following the
            link, or by copying its content (up to 5000 characters) and clicking "Generate topics
            from ..."
          </Typography>
          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2 }}>
            You will be presented with 3 options to choose from which you can create a debate, which
            you can edit if necessary.
          </Typography>
          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 4 }}>
            You can also re-generate debate topics from the same link/text.
          </Typography>
          <GenerateTopicsForm />
        </Paper>

        {error && (
          <Paper
            elevation={1}
            sx={{ p: 2, mb: 4, borderRadius: 2, borderLeft: '4px solid #f44336' }}
          >
            <Typography color="error">{error}</Typography>
          </Paper>
        )}

        {suggestions.length > 0 && (
          <Paper elevation={2} sx={{ p: 4, borderRadius: 2 }}>
            <Typography variant="h5" component="h2" gutterBottom sx={{ fontWeight: 'bold', mb: 3 }}>
              {TOPICS_CONSTANTS.SUGGESTED_TOPICS_TITLE}
            </Typography>
            <Grid container spacing={3}>
              {suggestions.map((suggestion, index) => (
                <Grid item xs={12} md={4} key={index}>
                  <Card
                    sx={{
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      transition: 'transform 0.2s, box-shadow 0.2s',
                      borderRadius: 2,
                      overflow: 'hidden',
                      '&:hover': {
                        transform: 'translateY(-5px)',
                        boxShadow: 6,
                        cursor: 'pointer',
                      },
                    }}
                    onClick={() => handleCreateDebate(suggestion)}
                  >
                    <CardContent sx={{ flexGrow: 1, p: 3 }}>
                      <Typography
                        variant="h5"
                        component="h3"
                        gutterBottom
                        sx={{
                          fontWeight: 'bold',
                          mb: 2,
                          color: 'primary.main',
                          minHeight: '64px',
                          display: 'flex',
                          alignItems: 'center',
                          fontSize: '1.4rem',
                        }}
                      >
                        {suggestion.thesis}
                      </Typography>
                      <Divider sx={{ my: 2 }} />
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{
                          mb: 3,
                          minHeight: '80px',
                          fontSize: '0.95rem',
                          lineHeight: 1.5,
                        }}
                      >
                        {suggestion.description}
                      </Typography>
                      <Button
                        variant="maxSize"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleCreateDebate(suggestion);
                        }}
                      >
                        {TOPICS_CONSTANTS.CREATE_DEBATE_BUTTON_TEXT}
                      </Button>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Paper>
        )}
      </Box>
    </PageContainer>
  );
};

export default GenerateTopics;
